/**
 * AccountManager Terminal API client.
 *
 * Two endpoints fronted by the same Lambda:
 *  - getSummary: KPI block only — target p95 < 500 ms, used for above-fold render
 *  - getDetails: full pool + subscriptions + positions — target p95 < 2 s
 *
 * The page renders summary first, then layers details on top once they arrive
 * (tiered loading). SuperAdmins can pivot to another AM's pool via the
 * optional `accountManagerUserId` argument; for AMs it is ignored / 403.
 */
import apiClient from './apiClient';
import type { ApiResponse } from '../types';
import type { TerminalKpis, TerminalDetails } from '../types/accountManager';

const API_TYPE = 'api' as const;

function unwrap<T>(response: ApiResponse<T> | T, errorMsg: string): T {
  if ((response as any)?.success !== undefined) {
    const wrapped = response as ApiResponse<T>;
    if (!wrapped.success || !wrapped.data) {
      throw new Error(wrapped.message || errorMsg);
    }
    return wrapped.data;
  }
  return response as T;
}

class AccountManagerTerminalService {
  /**
   * KPI block only — used for the first paint of the terminal page.
   * Caller can poll this independently of `getDetails`.
   */
  async getSummary(accountManagerUserId?: string): Promise<TerminalKpis> {
    const query = accountManagerUserId
      ? `?account_manager_user_id=${encodeURIComponent(accountManagerUserId)}`
      : '';
    try {
      const response = await apiClient.get<ApiResponse<TerminalKpis> | TerminalKpis>(
        `/admin/account-manager/terminal/summary${query}`,
        undefined,
        API_TYPE,
      );
      return unwrap(response, 'Failed to load terminal summary');
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Failed to load terminal summary',
      );
    }
  }

  /**
   * Full pool roster + every subscription + every open position.
   */
  async getDetails(accountManagerUserId?: string): Promise<TerminalDetails> {
    const query = accountManagerUserId
      ? `?account_manager_user_id=${encodeURIComponent(accountManagerUserId)}`
      : '';
    try {
      const response = await apiClient.get<ApiResponse<TerminalDetails> | TerminalDetails>(
        `/admin/account-manager/terminal/details${query}`,
        undefined,
        API_TYPE,
      );
      return unwrap(response, 'Failed to load terminal details');
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Failed to load terminal details',
      );
    }
  }
}

const accountManagerTerminalService = new AccountManagerTerminalService();
export default accountManagerTerminalService;
export { AccountManagerTerminalService };
